import * as React from 'react';

import type {Assignable} from 'nutshell-core/types';

import type {GetDefaultTask_taskDefaults as TaskDefaults} from 'nutshell-graphql-types';

import {colors} from '../../colors/colors';
import {HourglassRegularIcon} from '../../icon/svg/hourglass-regular-icon';
import {InfoIcon} from '../../icon/svg/info-icon';
import {SkipArrowIcon} from '../../icon/svg/skip-arrow-icon';
// @ts-expect-error - imported from .js/.jsx file
import {StepIcon} from '../../icon/svg/step-icon';
import {TaskRegularAiIcon} from '../../icon/svg/task-regular-ai-icon';
import {TaskTickCompleteIcon} from '../../icon/svg/task-tick-complete';
import {UnskipArrowIcon} from '../../icon/svg/unskip-arrow-icon';
import type {ToolbarOption} from '../../toolbar/toolbar';

import type {TaskRowItem} from './types';

export const getAdditionalInfo = (
    isCompleted: boolean,
    canBeCompleted: boolean | null | undefined,
    requirementText: string | null | undefined,
    isAutomated: boolean,
    isSkipped?: boolean | null,
    isAiGenerated?: boolean,
    creatorEntityName?: string | null
): React.ReactElement | null => {
    if (isAiGenerated && NutClientConfig.hasAiFollowUpTasksFF) {
        const tooltip = creatorEntityName
            ? `Generated from<br/>${creatorEntityName}<br/>as a follow-up`
            : 'AI-generated follow-up task';

        return (
            <div data-tip={tooltip} data-place='bottom' data-multiline={true}>
                {/* @ts-expect-error */}
                <TaskRegularAiIcon size={20} color={colors.aiPurplePrimary} />
            </div>
        );
    } else if (!canBeCompleted && requirementText && isAutomated && !isCompleted) {
        return (
            <div data-tip={requirementText} data-place='bottom'>
                <InfoIcon
                    size={15}
                    // @ts-expect-error
                    color={colors.blue}
                    useCurrentColor={true}
                    wrapWithDiv={false}
                />
            </div>
        );
    } else if (isAutomated || isSkipped) {
        return <StepIcon size={12} color={colors.orange} />;
    } else {
        return null;
    }
};

export const getAutomatedOptions = (
    isSkippable: boolean | null | undefined,
    isSkipped: boolean | null | undefined,
    isDelayable: boolean | null | undefined,
    isCompleteable: boolean | null | undefined,
    isCompleted: boolean | null | undefined,
    onSkipStep: () => void,
    onDelayStep: () => void,
    onCompleteStep: () => void,
    onUnskipStep: () => void,
    skipButtonRef: {
        current: HTMLElement | null | undefined;
    },
    unskipButtonRef: {
        current: HTMLElement | null | undefined;
    },
    delayButtonRef: {
        current: HTMLElement | null | undefined;
    }
): ToolbarOption[] => {
    const options = [];
    if (isSkippable && !isSkipped && !isCompleted) {
        options.push({
            id: 'skip',
            tooltipText: 'Skip task',
            icon: SkipArrowIcon,
            onClick: () => {
                onSkipStep();
            },
            // @ts-expect-error
            getRef: (ref) => {
                skipButtonRef.current = ref;
            },
        });
    }
    if (isDelayable && !isCompleted && !isSkipped) {
        options.push({
            id: 'delay',
            tooltipText: 'Delay task',
            icon: HourglassRegularIcon,
            onClick: () => {
                onDelayStep();
            },
            // @ts-expect-error
            getRef: (ref) => {
                delayButtonRef.current = ref;
            },
        });
    }
    if (isCompleteable && isSkipped && !isCompleted) {
        options.push({
            id: 'unskip',
            tooltipText: 'Unskip task',
            icon: UnskipArrowIcon,
            onClick: () => {
                onUnskipStep();
            },
            // @ts-expect-error
            getRef: (ref) => {
                unskipButtonRef.current = ref;
            },
        });
        options.push({
            id: 'complete',
            tooltipText: 'Complete task',
            icon: TaskTickCompleteIcon,
            onClick: () => {
                onCompleteStep();
            },
        });
    }

    return options;
};

// Get the assignee name given an assignee id
export const getAssigneeName = (assignables: Assignable[], assigneeId: string) => {
    const foundAssignee = assignables.find((a) => a.id === assigneeId);

    return foundAssignee ? foundAssignee.name : assigneeId;
};

export const mapTaskToDefaultTask = (task: TaskRowItem): TaskDefaults => {
    return {
        assignee: task.assignee,
        dueTime: task.dueTime,
        description: task.description,
        title: task.title,
        completedByUser: task.completedByUser,
        completedTime: task.completedTime,
        id: task.id,
        isOverdue: task.isOverdue,
        __typename: 'Task', // Will only ever be a Task
    };
};
